import { flushSync } from "react-dom";
import { Button } from "@/components/ui/button";
import { useTheme } from "./ThemeProvider";
import { SunMoon } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * One tap, no menu: whatever is showing, show the other (Andrew, 2026-10-05,
 * "like PropPay" — `flipTheme` in proppaylx-app's lib/theme.ts).
 *
 * A device with no stored choice follows the system, as ThemeProvider and
 * theme-init.js already do. A tap pins the OPPOSITE of what is on screen,
 * never "system": someone on a dark device who taps it wants light, and wants
 * it to stay light. The way back to following the device is Appearance in the
 * account menu, once signed in.
 *
 * Light ⇄ dark cross-fades as one image where the browser can (a view
 * transition of the root) and simply switches where it cannot or the reader
 * asked for less motion. The class has to be on <html> before the callback
 * returns, or the transition captures the old theme twice — so the state
 * update is flushed and the class set directly; ThemeProvider's effect then
 * agrees with it. A skipped transition (tab hidden, a second tap mid-fade)
 * rejects `ready`, which is expected noise.
 */
export function ThemeToggle({ variant = "ghost" }: { variant?: "ghost" | "outline" } = {}) {
  const { effectiveTheme, setTheme } = useTheme();
  const t = useTranslation();
  const dark = effectiveTheme === "dark";

  const flip = () => {
    const next = dark ? "light" : "dark";
    const apply = () => {
      flushSync(() => setTheme(next));
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(next);
    };
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const start = document.startViewTransition?.bind(document);
    // A hidden page has no frame to run the transition's callback in.
    if (!start || reduced || document.visibilityState !== "visible") {
      apply();
      return;
    }
    start(apply).ready.catch(() => {});
  };

  return (
    <Button
      type="button"
      variant={variant}
      size="icon"
      aria-pressed={dark}
      onClick={flip}
      data-testid="button-theme-toggle"
    >
      <SunMoon />
      <span className="sr-only">{t("shellThemeDarkMode", "Dark Mode")}</span>
    </Button>
  );
}
